import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useSearchParams } from "react-router";
import { AudienceTag, Button, ForAudience, FormRow, FormSection, Icon, Pill } from "@omnabase/ui";
import { SENDER_LINE_UNSAFE_MESSAGE, SenderLineSchema, type Project } from "@omnabase/shared";
import { senderName } from "@omnabase/shared/mail";
import { api } from "../../lib/api";
import { SenderPick, type SignAs } from "./SenderPick";
import { useFormState, useSaver } from "../../lib/save";
import { useSession } from "../../shell/store";
import { useFetch } from "../../lib/inbox";
import { useLive } from "../../lib/live";
import { useNavigate, useWs } from "../../lib/nav";
import { canProject } from "../../lib/access";
import { PlansAction } from "./PlanGate";
import { SendingDomainSetup } from "./SendingDomainSetup";
import { useScrollToCard } from "../../lib/scrollToCard";
import { ProjectSignature } from "./ProjectSignature";

/** The Mail card on a project (ported from the prototype's ProjectMail; M3 round six), as two numbered steps since 2026-10-13 (the
 *  founder's mockup): step 1 receives the support mail (the guided setup's state), step 2 chooses where replies are sent from, two ways
 *  only (founder's choice 2): an address on our domain, or your own domain with DNS records (M7 round seven: live on a plan with own
 *  domains, the setup below the radios; without the feature the plan that has it). Read from GET /projects/:id/mail and refetched when the
 *  setup changes (project.updated); the mock-up's From line is the card's sending address. The two steps come first, then "Sender and
 *  signature" (the founder's order, 2026-10-21; the signature since 2026-10-26). */
export type MailCard = {
  inbound: { address: string; host: string; state: "pending" | "confirmed"; startedAt: string; confirmedAt: string | null; code?: string | null; confirmLink?: string | null; testToken?: string; testSentAt: string | null; adminSentTo: string | null; adminSentAt: string | null } | null;
  host: { id: string; name: string; verifies: boolean; needsAdmin: string; steps: string[] } | null;
  sending: { address: string; name: string; via: string };
  paused: { at: string; reason: string } | null;
  limits: { day: number | null; month: number | null; sentToday: number; sentThisMonth: number };
};
export const useMailCard = (slug: string, projectId: string) => {
  const card = useFetch<MailCard>(`/api/v1/w/${slug}/projects/${projectId}/mail`);
  useLive(slug, () => void card.reload(), (e) => e.kind === "project.updated");
  return card;
};

export function ProjectMailCard({ project: p }: { project: Project }) {
  const { workspace, me, reload, toast } = useSession();
  const { ws } = useWs();
  const card = useMailCard(workspace.slug, p.id);
  const sender = useSender(p);
  const inbound = card.data?.inbound ?? null;
  // one address receives (2026-10-01); replies leave from it, or (M7 round seven) from the card's own address while the domain is verified
  const reply = card.data?.sending.via === "own" ? card.data.sending.address : p.address;
  // the card again when the project's address or sending domain changes (not on opening: polish round two, the card was read once already)
  const sent = `${p.address}|${p.sendingDomainId}|${p.sendingLocal}`; const lastSent = useRef(sent);
  useEffect(() => { if (lastSent.current === sent) return; lastSent.current = sent; void card.reload(); }, [sent]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = async () => { await Promise.all([reload(), card.reload()]); };
  const [receiveStep, sendStep] = [useRef<HTMLElement>(null), useRef<HTMLElement>(null)];
  useCardFromLink(receiveStep, sendStep, card.data !== null);
  return (
    <>
    <FormSection title="Mail" intro="Two steps to answer support mail from OmnaBase.">
      <div className="ob-mail-steps">
        <MailStep n={1} title="Receive your support mail" done={inbound?.state === "confirmed"} innerRef={receiveStep}>
          <Receiving inbound={inbound} receive={p.address} setup={ws(`/settings/projects/${p.id}/connect-mail`)} />
        </MailStep>
        <MailStep n={2} title="Choose where your replies are sent from" tag={<AudienceTag audience="locked" />} innerRef={sendStep}>
          <RepliesFrom project={p} supportAddress={inbound?.address ?? null} onChanged={changed} />
        </MailStep>
      </div>
    </FormSection>
    <FormSection title="Sender and signature" audience="locked" intro="The name customers see as the sender, and the signature every reply from this project ends with.">
      <ProjectSignature project={p} from={`${sender.fromName(sender.signAs)} <${reply}>`}
        sender={<FormRow label="Sender" help="The name customers see as the sender. The AI's own automatic replies always use the team form."><SenderPick signAs={sender.signAs} line={sender.line} fromName={sender.fromName} disabled={!canProject(workspace, p)} onSignAs={sender.choose} onLine={sender.type} /></FormRow>} />
    </FormSection>
    </>
  );
}

/** "Signed as" (2026-10-01, per project): the From name customers see, previewed with the signed-in person's own names; the connector tags
 *  round (2026-10-27): the custom line, kept here while it is typed (the mock-up follows every key) and saved after a pause when it is safe */
function useSender(p: Project) {
  const { workspace, me, reload, toast } = useSession();
  const person = { displayName: workspace.me.names.displayName, customerName: workspace.me.names.customerName, name: me.user.name }; // the workspace's names (2026-10-01)
  const [state, setState] = useFormState({ signAs: (p.signAs ?? "agent") as SignAs, line: p.senderLine ?? "" }, p.id); // the full project carries them; the database's defaults otherwise
  const { save } = useSaver<{ signAs?: SignAs; senderLine?: string }>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/projects/${p.id}`, patch); await reload(); }, (m) => toast(m, { kind: "error" }));
  const fromName = (signAs: SignAs) => senderName({ signAs, senderLine: state.line, person, projectName: p.name, workspaceName: workspace.name, role: workspace.me.names.customerRole });
  const choose = (signAs: SignAs) => { setState((x) => ({ ...x, signAs })); save({ signAs }); };
  const type = (line: string) => {
    setState((x) => ({ ...x, line }));
    if (SenderLineSchema.safeParse(line).success) save({ senderLine: line });
    else toast(SENDER_LINE_UNSAFE_MESSAGE, { kind: "error" });
  };
  return { ...state, fromName, choose, type };
}

/** `?card=mail` (Home's "Connect mail") scrolls to the Mail card, `?card=sending` ("Set the address") to its sender step */
function useCardFromLink(receiveStep: RefObject<HTMLElement | null>, sendStep: RefObject<HTMLElement | null>, ready: boolean): void {
  const card = useSearchParams()[0].get("card");
  const target = useCallback(() => (card === "sending" ? sendStep.current : receiveStep.current?.closest<HTMLElement>(".ob-form__section") ?? null), [card, receiveStep, sendStep]);
  useScrollToCard(target, card === "mail" || card === "sending", ready);
}

/** One step of the card: the round number (a check once `done`), the heading with an optional tag, the step's body. Step 1 is filled and
 *  joined to step 2 by a line, both drawn by the kit (`.ob-mail-step`); the region is named "Step <n>: <heading>" like the guided setup's. */
function MailStep({ n, title, done, tag, innerRef, children }: { n: number; title: string; done?: boolean; tag?: ReactNode; innerRef?: RefObject<HTMLElement | null>; children: ReactNode }) {
  return (
    <section className="ob-mail-step" aria-label={`Step ${n}: ${title}`} ref={innerRef}>
      <span className="ob-mail-step__n" aria-hidden="true">{done ? <Icon name="check" size="sm" /> : n}</span>
      <div className="ob-mail-step__main">
        <div className="ob-mail-step__head"><h3 className="ob-mail-step__title">{title}</h3>{tag}</div>
        {children}
      </div>
    </section>
  );
}

/** Step 1's body: forward the support mail here. Before a setup the mockup's sentence names the address to forward to; while pending and
 *  once confirmed, the guided setup's words, with the way back into it. */
function Receiving({ inbound, receive, setup }: { inbound: MailCard["inbound"]; receive: string; setup: string }) {
  const navigate = useNavigate();
  if (!inbound) return <StartConnecting receive={receive} start={() => navigate(`${setup}?step=address`)} />;
  return (
    <div className="ob-mail-step__row">
      {inbound.state === "confirmed"
        ? <><p className="ob-mail-step__text">Mail to {inbound.address} is forwarded to {receive} and opens a ticket here.</p><span className="ob-mail-step__actions"><Pill>Forwarding confirmed</Pill><Button size="sm" onClick={() => navigate(setup)}>Set up again</Button></span></>
        : <><p className="ob-mail-step__text">Setting up {inbound.address}: the forwarding is not confirmed yet.</p><span className="ob-mail-step__actions"><Pill>Not confirmed yet</Pill><Button size="sm" onClick={() => navigate(setup)}>Continue the setup</Button></span></>}
    </div>
  );
}

/** Before a setup (the first setup round, item 3, 2026-10-22): the sentence, then "Start connecting" below it (full width on a phone) */
function StartConnecting({ receive, start }: { receive: string; start: () => void }) {
  return (
    <div className="ob-mail-step__start">
      <p className="ob-mail-step__text">Add one forwarding rule at your mail host so a copy of your support mail arrives at <strong>{receive}</strong>.</p>
      <span className="ob-mail-step__actions"><Button variant="primary" size="sm" icon="mail" onClick={start}>Start connecting</Button></span>
    </div>
  );
}

/** Step 2's body, the radio group "Replies come from" (its spoken name; the step's heading is the visible one): our domain, or the own
 *  domain (live on a plan with `ownDomains`; the setup shows once chosen, and stays while the project points at a domain, paused or not).
 *  Without the feature the second way is disabled and names the plan that has it. */
function RepliesFrom({ project: p, supportAddress, onChanged }: { project: Project; supportAddress: string | null; onChanged: () => Promise<void> }) {
  const { workspace, toast } = useSession();
  const canOwn = workspace.plan.features.ownDomains, canEdit = canProject(workspace, p);
  const [choseOwn, setChoseOwn] = useState(false);
  const own = !!p.sendingDomainId || choseOwn;
  const ours = async () => {
    setChoseOwn(false);
    if (!p.sendingDomainId) return;
    try { await api.patch(`/api/v1/w/${workspace.slug}/projects/${p.id}`, { sendingDomainId: null }); await onChanged(); } catch (e) { toast((e as Error).message, { kind: "error" }); }
  };
  const wayUp = workspace.plan.wayUp.ownDomains;
  return (
    <ForAudience audience="locked">
      <div role="radiogroup" aria-label="Replies come from" className="ob-mail-step__choices">
        <button type="button" role="radio" aria-checked={!own} aria-label="An address on our domain" className={`ob-choice-opt${own ? "" : " ob-choice-opt--on"}`} disabled={!canEdit} onClick={() => void ours()}>
          <span className="ob-choice-opt__radio" /><span className="ob-choice-opt__text"><span className="ob-choice-opt__title">An address on our domain</span><span className="ob-choice-opt__sub">{p.address}, under your name. Nothing to set up.</span></span>
        </button>
        <button type="button" role="radio" aria-checked={own} aria-label="Your own domain, with DNS records" className={`ob-choice-opt${own ? " ob-choice-opt--on" : ""}`} disabled={!canOwn || !canEdit} onClick={() => setChoseOwn(true)}>
          <span className="ob-choice-opt__radio" /><span className="ob-choice-opt__text"><span className="ob-choice-opt__title">Your own domain, with DNS records</span><span className="ob-choice-opt__sub">Replies leave from an address on your domain, such as support@example.com. Three records at your domain's DNS host; customers' answers still come back to the ticket.</span></span>
        </button>
      </div>
      {!canOwn && (
        <p className="ob-form-row__help ob-plan-gate">
          {wayUp ? <>Own domains are part of {wayUp.name} and up.</> : <>Own domains are not part of {workspace.plan.name}.</>}{" "}
          <PlansAction />
        </p>
      )}
      {own && <SendingDomainSetup project={p} canOwn={canOwn} canEdit={canEdit} supportAddress={supportAddress} onChanged={onChanged} />}
    </ForAudience>
  );
}
